import { Input, RadioButton } from "@tql/ui-components-react/kendo/inputs";
import { Label } from "@tql/ui-components-react/kendo/labels";

import type { PaymentMethodFormErrors } from "../../hooks/use-payment-method-form-validation.ts";
import {
	fieldErrorBorderClass,
	FieldErrorMessage,
} from "../field-error/field-error.tsx";
import styles from "./ach-form-inputs.module.css";

export const AchFormInputs = ({
	newPaymentValues,
	handleSetNewPaymentValue,
	errors,
}: {
	newPaymentValues: {
		bankName: string;
		bankAccountNumber: string;
		routingNumber: string;
		accountHolderName: string;
		ownershipType: string;
		accountType: string;
	};
	handleSetNewPaymentValue: (field: string, value: string) => void;
	errors: PaymentMethodFormErrors;
}) => {
	const routingNumberError =
		errors.routingNumberMissing || errors.routingNumberInvalid;
	const bankAccountNumberError =
		errors.bankAccountNumberMissing || errors.bankAccountNumberInvalid;

	return (
		<>
			<div className={styles.radioGroupRow}>
				<div>
					<Label>Ownership Type</Label>
					<div className={styles.radioOptions}>
						<RadioButton
							checked={newPaymentValues.ownershipType === "personal"}
							label={"Personal"}
							name={"ownershipType"}
							onChange={() =>
								handleSetNewPaymentValue("ownershipType", "personal")
							}
							value={"personal"}
						/>
						<RadioButton
							checked={newPaymentValues.ownershipType === "business"}
							label={"Business"}
							name={"ownershipType"}
							onChange={() =>
								handleSetNewPaymentValue("ownershipType", "business")
							}
							value={"business"}
						/>
					</div>
				</div>
				<div>
					<Label>Account Type</Label>
					<div className={styles.radioOptions}>
						<RadioButton
							checked={newPaymentValues.accountType === "checking"}
							label={"Checking"}
							name={"accountType"}
							onChange={() =>
								handleSetNewPaymentValue("accountType", "checking")
							}
							value={"checking"}
						/>
						<RadioButton
							checked={newPaymentValues.accountType === "savings"}
							label={"Savings"}
							name={"accountType"}
							onChange={() =>
								handleSetNewPaymentValue("accountType", "savings")
							}
							value={"savings"}
						/>
					</div>
				</div>
			</div>
			<div className={styles.field}>
				<Label>
					Account Holder Name <span className={"required"}>&nbsp;*</span>
				</Label>
				<Input
					className={
						errors.accountHolderName ? fieldErrorBorderClass : undefined
					}
					onChange={(e) =>
						handleSetNewPaymentValue(
							"accountHolderName",
							e.target.value as string,
						)
					}
					value={newPaymentValues.accountHolderName}
				/>
				{errors.accountHolderName ? <FieldErrorMessage /> : null}
			</div>
			<div className={styles.field}>
				<Label>
					Bank Name <span className={"required"}>&nbsp;*</span>
				</Label>
				<Input
					className={errors.bankName ? fieldErrorBorderClass : undefined}
					onChange={(e) =>
						handleSetNewPaymentValue("bankName", e.target.value as string)
					}
					value={newPaymentValues.bankName}
				/>
				{errors.bankName ? <FieldErrorMessage /> : null}
			</div>
			<div className={styles.field}>
				<Label>
					Account Number <span className={"required"}>&nbsp;*</span>
				</Label>
				<Input
					className={bankAccountNumberError ? fieldErrorBorderClass : undefined}
					inputMode={"numeric"}
					onChange={(e) =>
						handleSetNewPaymentValue(
							"bankAccountNumber",
							(e.target.value as string).replaceAll(/\D/g, ""),
						)
					}
					value={newPaymentValues.bankAccountNumber}
				/>
				{errors.bankAccountNumberMissing ? <FieldErrorMessage /> : null}
				{errors.bankAccountNumberInvalid ? (
					<FieldErrorMessage>
						Account number must be 4-17 digits
					</FieldErrorMessage>
				) : null}
			</div>
			<div>
				<Label>
					Routing Number <span className={"required"}>&nbsp;*</span>
				</Label>
				<Input
					className={routingNumberError ? fieldErrorBorderClass : undefined}
					inputMode={"numeric"}
					onChange={(e) =>
						handleSetNewPaymentValue(
							"routingNumber",
							(e.target.value as string).replaceAll(/\D/g, ""),
						)
					}
					value={newPaymentValues.routingNumber}
				/>
				{errors.routingNumberMissing ? <FieldErrorMessage /> : null}
				{errors.routingNumberInvalid ? (
					<FieldErrorMessage>
						Enter a valid 9-digit routing number
					</FieldErrorMessage>
				) : null}
			</div>
		</>
	);
};
